import { inject } from '@angular/core';
import {
  ActivatedRouteSnapshot,
  PRIMARY_OUTLET,
  RedirectCommand,
  ResolveFn,
  Router,
  RouterStateSnapshot,
} from '@angular/router';
import { map, tap } from 'rxjs';

import { UserApiService } from '../api/services/user-api.service';
import { MainResolvedData } from '../main/main.abstraction';
import { OrganizationStorageService } from '../organizations/organization-storage.service';
import { type Organization } from '../organizations/organizations.abstraction';
import { OrganizationsUtility } from '../organizations/organizations.utility';
import { RouteFragment, RouteParam } from '../shared/common';
import { UserStorageService } from '../shared/services/user/user-storage.service';
import { type UserProfile } from '../shared/services/user/user.abstraction';

import { PermissionsService } from './permissions.service';

export class MainRoutingUtility {
  static getScopedRouteFragments(router: Router, url: string): string[] {
    const segments = MainRoutingUtility.getPrimaryPathSegments(router, url);
    const [, , , ...routeFragments] = segments;

    return routeFragments;
  }

  static mainResolver(
    _route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): ReturnType<ResolveFn<MainResolvedData>> {
    const organizationStorageService = inject(OrganizationStorageService);
    const permissionsService = inject(PermissionsService);
    const router = inject(Router);
    const userApiService = inject(UserApiService);
    const userStorageService = inject(UserStorageService);
    const profile = userStorageService.getProfileSignal()();

    return userApiService.getOrganizations().pipe(
      map(({ items }) => {
        const resolvedOrganizations = items.map((organization) =>
          OrganizationsUtility.toOrganization(organization),
        );

        return {
          organizations: resolvedOrganizations,
        };
      }),
      map((resolvedData) =>
        MainRoutingUtility.getMainRootRedirectCommand(
          resolvedData.organizations,
          permissionsService,
          router,
          state.url,
          organizationStorageService.organization(),
          profile,
        ) ?? resolvedData,
      ),
      tap((resolvedData) => {
        if (resolvedData instanceof RedirectCommand) {
          return;
        }

        const shouldStoreOrganization = !MainRoutingUtility.isScopedMainUrl(router, state.url);

        if (shouldStoreOrganization) {
          organizationStorageService.setOrganization(
            MainRoutingUtility.getPreferredOrganization(
              resolvedData.organizations,
              organizationStorageService.organization(),
            ),
          );
        }
      }),
    );
  }

  static organizationResolver(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot,
  ): ReturnType<ResolveFn<Organization | null>> {
    const organizationStorageService = inject(OrganizationStorageService);
    const permissionsService = inject(PermissionsService);
    const router = inject(Router);
    const mainData = MainRoutingUtility.getMainResolvedData(route);

    if (!mainData) {
      return null;
    }

    const organizationTenantKey = route.paramMap.get(RouteParam.OrganizationTenantKey);
    const organization = mainData.organizations.find(
      ({ tenantKey }) => tenantKey === organizationTenantKey,
    );

    if (organization) {
      organizationStorageService.setOrganization(organization);

      const routeFragments = MainRoutingUtility.getScopedRouteFragments(router, state.url);
      const defaultRouteFragments =
        permissionsService.getDefaultOrganizationRouteFragments(organization);

      const hasRouteFragments = Boolean(routeFragments.length);

      if (!hasRouteFragments && defaultRouteFragments.length) {
        return MainRoutingUtility.createOrganizationRedirectCommand(
          router,
          organization,
          defaultRouteFragments,
        );
      }

      const canAccessOrganizationPage =
        permissionsService.canAccessOrganizationPage(organization, routeFragments);
      const shouldRedirectToMain = hasRouteFragments && !canAccessOrganizationPage;

      if (shouldRedirectToMain) {
        return MainRoutingUtility.createMainRedirectCommand(router);
      }

      return organization;
    }

    const firstOrganization = mainData.organizations[0];

    if (!firstOrganization) {
      organizationStorageService.setOrganization(null);

      return null;
    }

    const routeFragments = MainRoutingUtility.getScopedRouteFragments(router, state.url);
    const canAccessRouteFragments =
      Boolean(routeFragments.length)
      && permissionsService.canAccessOrganizationPage(firstOrganization, routeFragments);

    const safeRouteFragments =
      canAccessRouteFragments
        ? routeFragments
        : permissionsService.getDefaultOrganizationRouteFragments(firstOrganization);

    if (!safeRouteFragments.length) {
      return MainRoutingUtility.createMainRedirectCommand(router);
    }

    return MainRoutingUtility.createOrganizationRedirectCommand(
      router,
      firstOrganization,
      safeRouteFragments,
    );
  }

  private static createAdminRedirectCommand(router: Router): RedirectCommand {
    const urlTree = router.createUrlTree(['/', RouteFragment.Main, RouteFragment.Admin]);

    return new RedirectCommand(urlTree);
  }

  private static createMainRedirectCommand(router: Router): RedirectCommand {
    const urlTree = router.createUrlTree(['/', RouteFragment.Main]);

    return new RedirectCommand(urlTree);
  }

  private static createNoRoleRedirectCommand(router: Router): RedirectCommand {
    const urlTree = router.createUrlTree(['/', RouteFragment.NoRole]);

    return new RedirectCommand(urlTree);
  }

  private static createOrganizationRedirectCommand(
    router: Router,
    organization: Organization,
    routeFragments: string[],
  ): RedirectCommand {
    const commands = [
      '/',
      RouteFragment.Main,
      RouteFragment.Organization,
      organization.tenantKey,
      ...routeFragments,
    ];
    const urlTree = router.createUrlTree(commands);

    return new RedirectCommand(urlTree);
  }

  private static getLandingRedirectCommand(
    organizations: Organization[],
    permissionsService: PermissionsService,
    router: Router,
    selectedOrganization: Organization | null,
    profile: UserProfile | null,
  ): RedirectCommand | null {
    const firstOrganization = MainRoutingUtility.getPreferredOrganization(
      organizations,
      selectedOrganization,
    );
    const canViewAdminPanel = Boolean(profile?.permissions.canViewAdminPanel);
    const adminRedirectCommand = canViewAdminPanel
      ? MainRoutingUtility.createAdminRedirectCommand(router)
      : null;

    if (!firstOrganization) {
      return adminRedirectCommand;
    }

    const defaultRouteFragments =
      permissionsService.getDefaultOrganizationRouteFragments(firstOrganization);

    return defaultRouteFragments.length
      ? MainRoutingUtility.createOrganizationRedirectCommand(
        router,
        firstOrganization,
        defaultRouteFragments,
      )
      : adminRedirectCommand;
  }

  private static getMainResolvedData(route: ActivatedRouteSnapshot): MainResolvedData | null {
    const mainData = route.pathFromRoot.find(({ data }) => 'main' in data)?.data[
      'main'
      ] as MainResolvedData | null;

    return mainData ?? null;
  }

  private static getMainRootRedirectCommand(
    organizations: Organization[],
    permissionsService: PermissionsService,
    router: Router,
    url: string,
    selectedOrganization: Organization | null,
    profile: UserProfile | null,
  ): RedirectCommand | null {
    const segments = MainRoutingUtility.getPrimaryPathSegments(router, url);
    const [firstSegment, routeSegment] = segments;
    const isMainRootRoute = firstSegment === RouteFragment.Main && !routeSegment;
    const isNoRoleRoute = firstSegment === RouteFragment.NoRole && !routeSegment;

    if (!isMainRootRoute && !isNoRoleRoute) {
      return null;
    }

    const landingRedirect = MainRoutingUtility.getLandingRedirectCommand(
      organizations,
      permissionsService,
      router,
      selectedOrganization,
      profile,
    );

    if (isNoRoleRoute) {
      return landingRedirect;
    }

    return landingRedirect ?? MainRoutingUtility.createNoRoleRedirectCommand(router);
  }

  private static getPreferredOrganization(
    organizations: Organization[],
    selectedOrganization: Organization | null,
  ): Organization | null {
    const preferredOrganization = organizations.find(
      ({ tenantKey }) => tenantKey === selectedOrganization?.tenantKey,
    );

    return preferredOrganization ?? organizations[0] ?? null;
  }

  private static getPrimaryPathSegments(router: Router, url: string): string[] {
    const urlTree = router.parseUrl(url);

    return urlTree.root.children[PRIMARY_OUTLET]?.segments.map(({ path }) => path) ?? [];
  }

  private static isScopedMainUrl(router: Router, url: string): boolean {
    const segments = MainRoutingUtility.getPrimaryPathSegments(router, url);
    const [mainSegment, organizationSegment, organizationTenantKey] = segments;
    const isMainRoute = mainSegment === RouteFragment.Main;
    const isOrganizationRoute = organizationSegment === RouteFragment.Organization;
    const hasOrganizationTenantKey = Boolean(organizationTenantKey);
    const isScopedMainUrl = isMainRoute && isOrganizationRoute && hasOrganizationTenantKey;

    return isScopedMainUrl;
  }
}
